export function betTypeLabel(betType: string): string {
  // Every game prefixes its wire codes, so every game needs stripping here or an
  // operator reads "BAC BANKER PAIR" in a transaction row. Prefixes are listed
  // rather than stripped generically (e.g. /^[A-Z]+_/) because a generic rule
  // would also eat the leading word of an unprefixed code.
  // Crash encodes the player's TARGET in the bet type — `CRASH_250` is a bet on
  // 2.50x — because the place-bet body carries no free payload. Stripping the
  // prefix generically would leave an operator reading "250", so it is decoded
  // back into the multiplier it means.
  const crashTarget = /^CRASH_([1-9][0-9]*)$/.exec(betType);
  if (crashTarget) return `${(Number(crashTarget[1]) / 100).toFixed(2)}x`;

  return betType
    .replace(/^SICBO_/, '')
    .replace(/^DT_/, '')
    .replace(/^BAC_/, '')
    .replace(/^SDR_/, '')
    .replace(/^LDR_/, '')
    .replace(/_/g, ' ');
}

export function formatResult(gameId: string, resultData: unknown): string {
  const r = resultData as Record<string, unknown> | null;
  if (!r || typeof r !== 'object') return String(resultData ?? '—');
  if (gameId === 'sicbo' && Array.isArray((r as { dice?: unknown }).dice)) {
    const dice = (r as { dice: number[] }).dice;
    return `🎲 ${dice.join('·')} = ${(r as { total?: number }).total ?? dice.reduce((a, b) => a + b, 0)}`;
  }
  if (gameId === 'dragontiger' && typeof (r as { winner?: unknown }).winner === 'string') {
    const crit = (r as { critMultiplier?: number }).critMultiplier ?? 1;
    return `${(r as { winner: string }).winner}${crit > 1 ? ` ×${crit}` : ''}`;
  }
  if (gameId === 'greedyking' && typeof (r as { primary?: unknown }).primary === 'string') {
    const special = (r as { special?: string | null }).special;
    return `${(r as { primary: string }).primary}${special ? ` + ${special}` : ''}`;
  }
  if (gameId === 'baccarat' && typeof (r as { winner?: unknown }).winner === 'string') {
    // Player/Banker totals are what an operator is reconciling against, and the
    // pair flags decide whether a side bet paid — so both belong in the row.
    // Without this branch the cell printed a truncated JSON blob.
    const w = (r as { winner: string }).winner;
    const pt = (r as { playerTotal?: number }).playerTotal ?? '?';
    const bt = (r as { bankerTotal?: number }).bankerTotal ?? '?';
    const flags: string[] = [];
    if ((r as { natural?: boolean }).natural) flags.push('natural');
    if ((r as { playerPair?: boolean }).playerPair) flags.push('P pair');
    if ((r as { bankerPair?: boolean }).bankerPair) flags.push('B pair');
    return `${w} · P ${pt} / B ${bt}${flags.length ? ` · ${flags.join(', ')}` : ''}`;
  }
  if (
    (gameId === 'shortdicerush' || gameId === 'longdicerush') &&
    Array.isArray((r as { dice?: unknown }).dice)
  ) {
    // Both tables classify a roll by total and parity, and never void on a
    // triple — so the row shows what an operator is actually reconciling
    // against: the faces, the total, and the two tags that decided the board.
    const dice = (r as { dice: number[] }).dice;
    const total = (r as { total?: number }).total ?? dice.reduce((a, b) => a + b, 0);
    const size = (r as { isBig?: boolean }).isBig ? 'BIG' : 'SMALL';
    const parity = (r as { isOdd?: boolean }).isOdd ? 'ODD' : 'EVEN';
    const triple = (r as { isTriple?: boolean }).isTriple ? ' · triple' : '';
    return `🎲 ${dice.join('·')} = ${total} · ${size} ${parity}${triple}`;
  }
  if (gameId === 'crash' && typeof (r as { crashBps?: unknown }).crashBps === 'number') {
    // One number per round, and the two boundaries an operator reconciles
    // against are worth naming rather than leaving them to be read off the
    // decimals: 1.00x paid nobody, 10000.00x is the ceiling.
    const bps = (r as { crashBps: number }).crashBps;
    const x = `${(bps / 10_000).toFixed(2)}x`;
    if (bps <= 10_000) return `${x} · bust`;
    if (bps >= 100_000_000) return `${x} · max`;
    return x;
  }
  if (gameId === 'singaporelottery' && Array.isArray((r as { digits?: unknown }).digits)) {
    const digits = (r as { digits: number[] }).digits;
    const sum = (r as { sum?: number }).sum ?? digits.reduce((a, b) => a + b, 0);
    return `${digits.join('·')} · sum ${sum}`;
  }
  // Unknown game: show something rather than nothing, but never the whole blob.
  // singaporelottery's result carries ~20 derived boolean flags, and dumping it
  // raw stretched the history row far past the viewport and forced the entire
  // page to scroll sideways. Truncating keeps an unmapped game readable and
  // keeps the table's geometry intact.
  const json = JSON.stringify(r);
  return json.length > 80 ? `${json.slice(0, 80)}…` : json;
}
